"use client";

import { ComponentFilters } from "@utils/damac/api";
import { useTranslations } from "next-intl";
import { FC, Fragment, useEffect, useRef, useState } from "react";

import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "@/components/ui/Button/Button";
import { ctaClickTracking } from "@/dataLayer/dataLayer";
import { DL_KEYS } from "@/dataLayer/keys";
import useMediaQuery from "@/hooks/useMediaQuery";
import { Article, ArticleListingProps, Site, TargetSite } from "@/types";

import ArticleCard from "./ArticleCard";
import ArticleFilters from "./ArticleFilters";
import "./ArticleListing.css";
import EditorialBox from "./EditorialBox";
import filter from "./filter";
import loadArticles from "./loadArticles";
import loadMore from "./loadMore";

const ArticleListing: FC<ArticleListingProps> = ({ entry, filters, fallbackImage, site }) => {
  const [articles, setArticles] = useState<Article[]>(entry.articles || []);
  const [loadMoreButton, setLoadMoreButton] = useState<boolean>(true);
  const t = useTranslations();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const limit = isDesktop === false ? 5 : 13;
  console.log("[DEBUG] isDesktop:", isDesktop, "limit:", limit);
  const skipCountRef = useRef<number>(0);
  const loadMoreLimit = 8;

  const featuredSorting = entry.sorting === "Featured";

  const [articlesFilters, setArticlesFilters] = useState<ComponentFilters>(
    entry.articlesType
      ? {
          articleType: [
            {
              value: entry.articlesType,
            },
          ],
          target: [
            {
              value: TargetSite[site],
              operator: "[in]",
            },
          ],
        }
      : {
          target: [
            {
              value: TargetSite[site],
              operator: "[in]",
            },
          ],
        },
  );

  let loadMoreArticles = () => {
    console.log("[DEBUG] Load More - Skip:", skipCountRef.current, "Current articles count:", articles.length);
    loadMore(skipCountRef.current, loadMoreLimit, articlesFilters, featuredSorting).then((res) => {
      console.log("[DEBUG] Load Data", res);
      setArticles((prevArticles) => {
        const newArticles = prevArticles.concat(res);
        console.log("[DEBUG] New total articles:", newArticles.length);
        return newArticles;
      });
      skipCountRef.current = skipCountRef.current + loadMoreLimit;
      console.log("[DEBUG] Updated skip count:", skipCountRef.current);
      if (res?.length === 0) {
        setLoadMoreButton(false);
      }
    });
    ctaClickTracking(DL_KEYS?.viewMore, "AddListing", "loadMoreArticles", true);
  };

  useEffect(() => {
    loadArticles(limit, entry.articlesType, featuredSorting).then((res) => {
      console.log("[DEBUG] Initial Load Data - Requested:", limit, "Received:", res?.length, "Articles:", res);
      setArticles(res);
      skipCountRef.current = limit;
      console.log("[DEBUG] Initial skip count set to:", skipCountRef.current);
      if (res?.length === 0) {
        setLoadMoreButton(false);
      }
    });
  }, [limit, isDesktop, entry.articlesType, featuredSorting]);

  const filterArticles = (e: any) => {
    let filters = articlesFilters;

    switch (e.target.name) {
      case "topic": {
        if (e.target.value == "") {
          delete filters.tags;
        } else {
          filters.tags = [
            {
              value: e.target.value,
              operator: "[in]",
            },
          ];
        }
        break;
      }
      case "year": {
        let yearSelects = document.querySelectorAll("select[name=year]");
        let index = 0;
        yearSelects.forEach((element, i) => {
          if (element == e.target) {
            index = i;
            return;
          }
        });
        let selectMonth = document.querySelectorAll("select[name=month]")[index] as HTMLSelectElement;
        selectMonth.selectedIndex = 0;
        if (e.target.value.toString() === "") {
          delete filters.postDate;
        } else {
          filters.postDate = [
            {
              value: new Date(e.target.value.toString()).toISOString(),
              operator: "[gte]",
            },
            {
              value: new Date((parseInt(e.target.value) + 1).toString()).toISOString(),
              operator: "[lt]",
            },
          ];
        }
        break;
      }
      case "month": {
        let monthSelects = document.querySelectorAll("select[name=month]");
        let index = 0;
        monthSelects.forEach((element, i) => {
          if (element == e.target) {
            index = i;
            return;
          }
        });
        let selectYear = document.querySelectorAll("select[name=year]")[index] as HTMLSelectElement;
        let currentYear = new Date().getFullYear();
        let yearToSet;
        while (!yearToSet) {
          for (let i = 0; i < selectYear.options.length; i++) {
            if (selectYear.options[i].value === currentYear.toString()) {
              yearToSet = currentYear.toString();
              break;
            }
          }
          currentYear--;
        }
        let date = new Date(yearToSet);
        date.setMonth(e.target.value);
        if (e.target.value == "") {
          if (selectYear && selectYear.options[selectYear.selectedIndex].value !== "") {
            filters.postDate = [
              {
                value: new Date(selectYear.options[selectYear.selectedIndex].value).toISOString(),
                operator: "[gte]",
              },
              {
                value: new Date((parseInt(selectYear.options[selectYear.selectedIndex].value) + 1).toString()).toISOString(),
                operator: "[lt]",
              },
            ];
          } else {
            delete filters.postDate;
          }
        } else {
          if (date > new Date()) {
            if (selectYear && selectYear.selectedIndex == 0) {
              for (let i = 0; i < selectYear.options.length; i++) {
                if (selectYear.options[i].value === date.getFullYear().toString()) {
                  selectYear.selectedIndex = i;
                }
              }
            }
          } else {
            if (selectYear && selectYear.selectedIndex == 0) {
              for (let i = 0; i < selectYear.options.length; i++) {
                if (selectYear.options[i].value === date.getFullYear().toString()) {
                  selectYear.selectedIndex = i;
                }
              }
            }
          }
          if (selectYear.options[selectYear.selectedIndex].value !== "") {
            date.setFullYear(parseInt(selectYear.options[selectYear.selectedIndex].value));
          }
          filters.postDate = [
            {
              value: date.toISOString(),
              operator: "[gte]",
            },
          ];
          date.setMonth(date.getMonth() + 1);
          filters.postDate.push({
            value: date.toISOString(),
            operator: "[lt]",
          });
        }
        break;
      }
      case "media": {
        if (e.target.value == "") {
          delete filters.video;
        } else {
          filters.video = [
            {
              value: e.target.value,
              operator: "[exists]",
            },
          ];
        }
        break;
      }
    }

    setArticlesFilters(filters);

    filter(articlesFilters, limit, featuredSorting).then((res) => {
      setArticles(res);
      skipCountRef.current = res?.length || 0;
      if (res?.length >= limit) {
        setLoadMoreButton(true);
      } else {
        setLoadMoreButton(false);
      }
    });
  };

  return (
    <>
      {entry.variant != "offers" && (
        <ArticleFilters
          articleType={entry.articlesType}
          filters={filters}
          filterFunction={filterArticles}
          setArticles={setArticles}
          setArticlesFilters={setArticlesFilters}
          featuredSorting={featuredSorting}
        />
      )}
      <div className="py-l md:py-xxl">
        <div className="px-l md:p-0 md:w-20\/24 md:mx-auto md:grid md:grid-cols-4 md:gap-l">
          {articles?.map((article, i) => (
            <Fragment key={article.slug}>
              {entry.editorialBox && entry.variant === "offers" && i == 10 && <EditorialBox editorialBox={entry.editorialBox} />}
              <ArticleCard
                article={article}
                isFirstArticle={entry.variant === "articles" && i === 0}
                isOffer={entry.variant === "offers"}
                fallbackImage={fallbackImage}
              />
            </Fragment>
          ))}
        </div>
      </div>
      {loadMoreButton && (
        <div className="text-center pb-l">
          <Button
            size={ButtonSize.MEDIUM}
            variant={ButtonVariant.PRIMARY}
            theme={ButtonTheme.WHITE}
            onClick={() => {
              loadMoreArticles();
            }}
            customClass="mt-m font-medium"
          >
            {t("view.more")}
          </Button>
        </div>
      )}
    </>
  );
};

export default ArticleListing;
